import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, withCtx as _withCtx, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, createBlock as _createBlock } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "fixed bottom-4 right-4 z-[200]"
}
const _hoisted_2 = {
  key: 1,
  class: "fixed top-[73px] inset-x-0 z-[200] flex justify-center px-4 pt-2 pointer-events-none"
}
const _hoisted_3 = { class: "flex items-center justify-between gap-3" }
const _hoisted_4 = {
  key: 0,
  "aria-hidden": "true"
}

import { computed, onBeforeUnmount, ref, watch } from 'vue';
import { useBulkInvoiceCreateStore } from '@/stores/billing/useBulkInvoiceCreateStore';
import { getPinia } from '@/createPiniaSingleton';
import AlertBar from '@/components/AlertBar.vue';
import LlSecondaryButton from '@/components/LlSecondaryButton.vue';

// Mounted once, globally (see bulkInvoiceCreateBanner.ts) -- a run survives navigating away
// from Ready to Bill, so this has to be visible regardless of which route is active.
const PROGRESS_COLOR = 'primary';

const SUMMARY_VISIBLE_MS = 8000;

export default /*@__PURE__*/_defineComponent({
  __name: 'BulkInvoiceCreateBanner',
  setup(__props) {

const store = useBulkInvoiceCreateStore(getPinia());

// Matches tailwind.config.js's green.light / bg-green-light, the same green as the "Create
// Draft Invoices" button that starts this run -- Vuetify's own `type="info"` blue reads as an
// unrelated status color here, not "the thing you just clicked is still going".
const showSummary = ref(false);
let summaryTimeout: ReturnType<typeof setTimeout> | null = null;

// Collapsed is a per-viewer display preference, not run state -- it lives here, not in the
// store. Only meaningful while creating (see effectivelyCollapsed): the completion summary
// always shows in full regardless, since a collapsed badge could otherwise hide a failure the
// user never explicitly dismissed.
const collapsed = ref(false);

watch(
  () => store.creating,
  (creating, wasCreating) => {
    if (creating && !wasCreating) {
      collapsed.value = false;
    }
    if (!creating && wasCreating) {
      showSummary.value = true;
      if (summaryTimeout) clearTimeout(summaryTimeout);
      summaryTimeout = setTimeout(() => {
        showSummary.value = false;
      }, SUMMARY_VISIBLE_MS);
    }
  },
);

// Progress otherwise only advances at chunk boundaries, which at a realistic per-invoice rate
// can be minutes apart -- this ticks `now` every second so elapsedLabel below reads as a live
// clock, telling a long run apart from a stuck one in between those updates.
const now = ref(Date.now());
let tickInterval: ReturnType<typeof setInterval> | null = null;
watch(
  () => store.creating,
  (creating) => {
    if (creating) {
      now.value = Date.now();
      tickInterval = setInterval(() => {
        now.value = Date.now();
      }, 1000);
    } else if (tickInterval) {
      clearInterval(tickInterval);
      tickInterval = null;
    }
  },
  { immediate: true },
);
onBeforeUnmount(() => {
  if (tickInterval) clearInterval(tickInterval);
});

const elapsedLabel = computed(() => {
  if (!store.creating || !store.runStartedAt) return '';
  const elapsedSeconds = Math.max(0, Math.floor((now.value - store.runStartedAt) / 1000));
  const minutes = Math.floor(elapsedSeconds / 60);
  const seconds = elapsedSeconds % 60;
  return minutes > 0 ? `${minutes}m ${seconds}s elapsed` : `${seconds}s elapsed`;
});

const visible = computed(() => store.creating || showSummary.value);
const effectivelyCollapsed = computed(() => collapsed.value && store.creating);

const alertType = computed(() => {
  if (store.creating) return 'info';
  const summary = store.lastRunSummary;
  if (!summary) return 'info';
  if (summary.failedCount === 0) return 'success';
  if (summary.failedCount === summary.total) return 'error';
  return 'warning';
});

// Only overrides color while creating -- the completion summary's success/warning/error
// colors are already meaningful status colors and shouldn't be repainted green.
const alertColor = computed(() => (store.creating ? PROGRESS_COLOR : undefined));

// This is the role="alert" live region's announced text -- elapsedLabel is deliberately not
// part of it (see the aria-hidden span in the template): it changes every second, and before
// this ticking clock existed, a screen reader only heard an update at chunk boundaries (minutes
// apart). Folding elapsedLabel in here would re-announce the whole banner once a second for the
// run's entire duration.
const bannerText = computed(() => {
  if (store.creating) {
    const kind = store.draft ? 'draft' : 'QuickBooks';
    const chunkSuffix =
      store.creatingChunkTotal > 1 ? ` (batch ${store.creatingChunk} of ${store.creatingChunkTotal})` : '';
    return `Creating ${store.creatingTotal} ${kind} invoice(s)${chunkSuffix}. You can safely navigate away -- this will keep running.`;
  }
  const summary = store.lastRunSummary;
  if (!summary) return '';
  if (summary.failedCount === 0) return summary.successMessagePlural;
  if (summary.failedCount === summary.total) {
    return `Failed to create ${summary.failedCount} invoice(s). See Ready to Bill for details.`;
  }
  return `${summary.total - summary.failedCount} of ${summary.total} invoice(s) created; ${summary.failedCount} failed. See Ready to Bill for details.`;
});

return (_ctx: any,_cache: any) => {
  return (visible.value && effectivelyCollapsed.value)
    ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
        _createVNode(LlSecondaryButton, {
          color: PROGRESS_COLOR,
          size: "small",
          loading: true,
          class: "shadow-lg",
          "aria-label": "Batch invoice creation in progress -- click to expand",
          "data-cy": "bulk-invoice-create-badge",
          onClick: _cache[0] || (_cache[0] = ($event: any) => (collapsed.value = false))
        }, {
          default: _withCtx(() => [
            _createTextVNode(_toDisplayString(_unref(store).creatingProcessed) + " / " + _toDisplayString(_unref(store).creatingTotal), 1)
          ]),
          _: 1
        })
      ]))
    : (visible.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
          _createVNode(AlertBar, {
            type: alertType.value,
            color: alertColor.value,
            class: "shadow-md max-w-xl w-full pointer-events-auto",
            "data-cy": "bulk-invoice-create-banner"
          }, {
            default: _withCtx(() => [
              _createElementVNode("div", _hoisted_3, [
                _createElementVNode("span", null, [
                  _createTextVNode(_toDisplayString(bannerText.value) + " ", 1),
                  (elapsedLabel.value)
                    ? (_openBlock(), _createElementBlock("span", _hoisted_4, "(" + _toDisplayString(elapsedLabel.value) + ")", 1))
                    : _createCommentVNode("", true)
                ]),
                (_unref(store).creating)
                  ? (_openBlock(), _createBlock(LlSecondaryButton, {
                      key: 0,
                      variant: "text",
                      color: "info",
                      size: "x-small",
                      class: "shrink-0",
                      "aria-label": "Collapse progress to a small badge",
                      "data-cy": "bulk-invoice-create-collapse",
                      onClick: _cache[1] || (_cache[1] = ($event: any) => (collapsed.value = true))
                    }, {
                      default: _withCtx(() => [...(_cache[2] || (_cache[2] = [
                        _createTextVNode(" Collapse ", -1)
                      ]))]),
                      _: 1
                    }))
                  : _createCommentVNode("", true)
              ])
            ]),
            _: 1
          }, 8, ["type", "color"])
        ]))
      : _createCommentVNode("", true)
}
}

})